import { RefObject, useContext, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import ReactDOM from "react-dom";
import { calculatePosition } from "utils/calculatePosition";
import ModalsManagerContext from "contexts/ModalsManager";
import { ModalFieldScope } from "components/ModalFieldScope";
import { useMediaQuery, useOnClickOutside } from "usehooks-ts";
import SignalsContext from "contexts/SignalsContext";
import EventContext from "contexts/EventContext";
import { motion } from "framer-motion";
import { Drawer } from "@mui/material";
import classNames from "classnames";

export enum DockPosition {
  TopLeft = 0,
  TopRight = 1,
  BottomLeft = 2,
  BottomRight = 3,
  DISABLED = -1,
  CENTER = 4,
  CENTER_2 = 5,
  FULLSCREEN_CENTER = 6,
}

const ModalLayout = ({
  name,
  refWrapper,
  children,
  customX = 250,
  customY = 10,
  dock = DockPosition.BottomRight,
  className = "",
  off,
  ignore = false,
  isAnimation = false,
  onEnd,
  toggle,
  disableMove = false,
  fullHeight = false,
  showBg = false,
  defaultWidth = 560,
}: {
  name?: string;
  refWrapper?: RefObject<HTMLDivElement>;
  children: any;
  customX?: number;
  customY?: number;
  dock?: DockPosition;
  className?: string;
  off?: boolean;
  ignore?: boolean;
  isAnimation?: boolean;
  onEnd?: () => void;
  toggle?: () => void;
  disableMove?: boolean;
  fullHeight?: boolean;
  widthAsLeftArea?: boolean;
  showBg?: boolean;
  defaultWidth?: number;
}) => {
  console.log("Modallayout");

  const [resizeWindow, setResizeWindow] = useState(false);
  const refs = useRef(null);
  const [position, setPosition] = useState<{
    x: number | null;
    y: number | null;
    width: number | null;
    height: number | null;
    actualPositionX: number | null;
    actualPositionY: number | null;
  }>({
    x: null,
    y: null,
    width: null,
    height: null,
    actualPositionX: 0,
    actualPositionY: 0,
  });
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [moveWindow, setMoveWindow] = useState(false);

  const { modals, setModals } = useContext(ModalsManagerContext);

  const [isStartAnimation, setIsStartAnimation] = useState(false);
  const [isEndAnimation, setIsEndAnimation] = useState(false);
  const {
    parent,
    children: modalChildren,
    setChildren,
  } = useContext(SignalsContext);

  let hidingTimeout: NodeJS.Timeout = null;

  useEffect(() => {
    if (parent === name && modalChildren.length) {
      setIsEndAnimation(true);
    }
  }, [parent, modalChildren]);

  useEffect(() => {
    if (!modals.hide || !isAnimation) return;

    setIsStartAnimation(false);
    setIsEndAnimation(true);

    if (parent === name && modalChildren.length) return;
    if (modalChildren === name) {
      const keys = Object.keys(modals);
      const index = keys.findIndex((f) => f === modalChildren);
      const copyOfModals = { ...modals };
      copyOfModals[keys[index]] = false;
      copyOfModals["hide"] = false;

      setChildren("");
      setModals(copyOfModals);
      return;
    }

    console.log("Init hiding timeout", modals);
    hidingTimeout = setTimeout(() => {
      console.log("Hiding timeout");
      if (onEnd) {
        onEnd();
      }

      setModals({
        createAction: false,
        createCase: false,
        createDossier: false,
        createEntity: false,
        createLetter: false,
        createPost: false,
        createPostage: false,
        editIncomingParcel: false,
        createCompany: false,
        createTask: false,
        createEvent: false,
        attachCase: false,
        editLabels: false,
        editStatus: false,
        hide: false,
        settings: false,
        addAddress: false,
        addClient: false,
        editPDFName: false,
        initialConfig: false,
        createChat: false,
        createHelpdesk: false,
        createInvoice: false,
        internalPass: false,
        decree: false,
        generateLetters: false,
        dispatch: false,
        confirmPickup: false,
        registerReception: false,
        menu: false,
        createMobile: false,
        contactFormSettings: false,
        recoveryCode: false,
        createBudgetExpense: false,
        createBudgetEngagement: false,
        createContractEntry: false,
        createInvoiceRegistryEntry: false,
      });
    }, 1000);
  }, [modals, position]);

  useEffect(() => {
    if (modals.hide) return;
    if (!isAnimation) return;
    if (position.actualPositionX === null || position.actualPositionY === null)
      return;

    setIsStartAnimation(true);
    setIsEndAnimation(false);
  }, [modals.hide]);

  useEffect(() => {
    if (hidingTimeout) {
      if (
        (modals.createAction ||
          modals.createCase ||
          modals.createCompany ||
          modals.createDossier ||
          modals.createEntity ||
          modals.createLetter ||
          modals.createPost ||
          modals.createPostage ||
          modals.createHelpdesk ||
          modals.createChat ||
          modals.createTask ||
          modals.createInvoice ||
          modals.createEvent) &&
        !modals.hide
      ) {
        console.log("Clearing modal hiding timeout");
        clearTimeout(hidingTimeout);
      }
    }
  }, [modals]);

  const handlePosition = () => {
    if (isMobile) return;

    if (ignore && refs?.current) {
      // @ts-ignore
      const modalSize = refs.current!.getBoundingClientRect();

      if (ignore) {
        const x = customX;
        const y = customY;

        if (!isMobile) {
          setPosition({
            x,
            y,
            width: defaultWidth,
            height: modalSize.height,
            actualPositionX: isAnimation ? -modalSize.width : x,
            actualPositionY: position.actualPositionY,
          });
        } else {
          setPosition({
            x: 0,
            y: 0,
            width: modalSize.width,
            height: modalSize.height * 0.8,
            actualPositionX: isAnimation ? -modalSize.width : x,
            actualPositionY: position.actualPositionY,
          });
        }
        return;
      }
    }

    if (
      (!refWrapper?.current || !refs.current) &&
      dock !== DockPosition.FULLSCREEN_CENTER
    ) {
      // @ts-ignore
      const modalSize = refs.current!.getBoundingClientRect();
      setPosition({
        x: 20,
        y: 60,
        width: 560,
        height: modalSize.height,
        actualPositionX: isAnimation ? -modalSize.width : 0,
        actualPositionY: position.actualPositionY,
      });

      return;
    }

    const position_2 = refWrapper?.current?.getBoundingClientRect();
    // @ts-ignore
    const modalSize = refs.current!.getBoundingClientRect();
    const screenWidth = window.outerWidth;
    const screenHeight = window.screen.height - 150;

    let x = position_2?.x ?? 0;
    let y = position_2?.y ?? 0;

    switch (dock) {
      case DockPosition.TopLeft:
        y = y - modalSize.height;

        if (x + modalSize.width > screenWidth) {
          x = x + position_2.width - modalSize.width;
        }

        break;
      case DockPosition.TopRight:
        y = y - modalSize.height;
        x = x + position_2.width - modalSize.width;

        break;
      case DockPosition.BottomLeft:
        y = y + position_2.height;

        if (x + modalSize.width > screenWidth) {
          x = x + position_2.width - modalSize.width;
        }

        break;
      case DockPosition.BottomRight:
        y = y + position_2.height;
        x = x + position_2.width - modalSize.width / 2;
        break;
      case DockPosition.CENTER:
        y = y + position_2.height / 2 - 50;
        x = x + position_2.width / 2 - (modalSize.width + 20);
        break;
      case DockPosition.CENTER_2:
        y = y + position_2.height / 2 - 50;
        x = x + position_2.width / 2 - modalSize.width;
        break;
      case DockPosition.DISABLED:
        x = x + customX;
        y = y - customY;

        if (!off && y + modalSize.height > screenHeight) {
          y = screenHeight - modalSize.height;
        }
        break;
      case DockPosition.FULLSCREEN_CENTER:
        console.log(
          `Screen width: ${screenHeight}, modalWidht: ${modalSize.width}`,
        );
        x = (screenWidth - modalSize.width) / 2;
        y = customY;

        setPosition({
          x: x,
          y: y,
          width: 560,
          height: modalSize.height,
          actualPositionX: isAnimation ? -modalSize.width : x,
          actualPositionY: position.actualPositionY,
        });
        return;
    }

    const { x: rx, y: ry } = calculatePosition(
      x,
      y,
      modalSize.width,
      modalSize.height,
    );

    setPosition({
      x: rx - 8,
      y: ry,
      width: 560,
      height: modalSize.height,
      actualPositionX: isAnimation ? -modalSize.width : x,
      actualPositionY: position.actualPositionY,
    });
  };

  useEffect(() => {
    handlePosition();
  }, []);

  const group =
    name === "createDossier" ||
    name === "createCase" ||
    name === "createAction" ||
    name === "createTask" ||
    name === "createEntity" ||
    name === "createCompany" ||
    name === "createLetter" ||
    name === "createPostage" ||
    name === "createPost"
      ? "first"
      : "second";

  const handleMouseMove = (e: any) => {
    if (moveWindow) {
      // @ts-ignore
      const position = refs.current!.getBoundingClientRect();

      let cx = 0;
      let cy = 0;

      if (!off) {
        const { x, y } = calculatePosition(
          e.clientX,
          e.clientY,
          position.width,
          position.height,
        );
        cx = x;
        cy = y;
      } else {
        cx = e.clientX - position.width / 2;
        cy = e.clientY - 30;
      }

      setPosition({
        x: cx,
        y: cy,
        width: position.width,
        height: position.height,
        actualPositionX: position.actualPositionX,
        actualPositionY: position.actualPositionY,
      });
    }

    if (resizeWindow) {
      let newWidth = e.clientX - 4 - position.x!;

      if (newWidth < 250) {
        newWidth = 350;
      }

      localStorage.setItem(`modalSize.${group}`, newWidth.toString());
      setPosition((prev) => ({
        ...prev,
        width: newWidth,
      }));
    }
  };

  let posX = position.x;
  let posY =
    isAnimation && position.actualPositionY
      ? position.actualPositionY
      : position.y;

  const opacity = position?.x ? 1 : 0;

  if (isAnimation && posY) {
    posY += 20;
  }

  if (!posX) {
    posX = 0;
  }

  if (!posY) {
    posY = 0;
  }

  const handleKeyDown = (e: any) => {
    if (e.code === "Escape") {
      toggle?.();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [moveWindow, resizeWindow]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setMoveWindow(false);
      setResizeWindow(false);
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("mouseleave", handleGlobalMouseUp);

    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mouseleave", handleGlobalMouseUp);
    };
  }, []);

  useOnClickOutside(refs, () => {
    toggle?.();
  });

  useEffect(() => {
    if (!position.height) return;
    const savedWidth = localStorage.getItem(`modalSize.${group}`);
    if (!savedWidth) return;

    setPosition({ ...position, width: Number(savedWidth) });
  }, [position.height]);

  const { setBlockClick } = useContext(EventContext);

  useEffect(() => {
    setBlockClick(true);

    return () => {
      setTimeout(() => {
        setBlockClick(false);
      }, 100);
    };
  }, []);

  const compareChildren = () => {
    if (!name && !fullHeight) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      );
    }

    return children;
  };

  if (isMobile && fullHeight) {
    return (
      <Drawer
        anchor={"bottom"}
        open={true}
        onClose={() => toggle?.()}
        sx={{
          ".MuiPaper-elevation": {
            background: "transparent !important",
            padding: "0 !important",
            border: "none !important",
          },
        }}
      >
        <div className={classNames(styles.childrens, styles.mobile)}>
          <ModalFieldScope>{children}</ModalFieldScope>
        </div>
      </Drawer>
    );
  }

  return ReactDOM.createPortal(
    <>
      {showBg && <div className={styles.bg}></div>}
      <div
        ref={refs}
        className={`${styles.window} ${
          moveWindow && styles.notSelectable
        } ${className} ${isStartAnimation && styles.show} ${
          isEndAnimation && styles.hide
        }`}
        style={{
          display: "flex",
          position: "absolute",
          left: `${posX}px`,
          top: `${fullHeight ? 8 : posY}px`,
          bottom: `${fullHeight ? "8px" : "auto"}`,
          zIndex: 1000,
          opacity: opacity,
          width: position.width ? `${position.width + 8}px` : undefined,
        }}
      >
        {!disableMove && (
          <div
            onMouseDown={() => {
              setMoveWindow(true);
            }}
            onMouseUp={() => setMoveWindow(false)}
            className={styles.scrollarea}
          ></div>
        )}
        <div className={styles.childrens}>
          <ModalFieldScope>{compareChildren()}</ModalFieldScope>
        </div>
        <div
          onMouseDown={() => setResizeWindow(true)}
          onMouseUp={() => setResizeWindow(false)}
          className={styles.resize}
        ></div>
      </div>
    </>,
    document.body,
  );
};

export default ModalLayout;
